class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}


customElements.define('cart-remove-button', CartRemoveButton);

window.CartActionDebug = window.CartActionDebug || {
  lastSummary: null,

  enabled() {
    try {
      return new URLSearchParams(window.location.search).get('cart_debug') === '1';
    } catch (_error) {
      return false;
    }
  },

  now() {
    return window.performance && typeof window.performance.now === 'function'
      ? window.performance.now()
      : Date.now();
  },

  start(type, meta = {}) {
    if (!this.enabled()) return null;

    const trace = {
      type,
      meta,
      startedAt: this.now(),
      lastAt: this.now(),
      steps: []
    };

    this.step(trace, 'start', meta);
    return trace;
  },

  step(trace, label, meta = {}) {
    if (!trace || !this.enabled()) return;

    const now = this.now();
    trace.steps.push({
      label,
      sinceStartMs: +(now - trace.startedAt).toFixed(2),
      deltaMs: +(now - trace.lastAt).toFixed(2),
      meta
    });
    trace.lastAt = now;
  },

  end(trace, status = 'ok', meta = {}) {
    if (!trace || !this.enabled()) return;

    this.step(trace, `end:${status}`, meta);

    const summary = {
      type: trace.type,
      status,
      totalMs: +Math.max(trace.lastAt - trace.startedAt, 0).toFixed(2),
      meta: trace.meta,
      steps: trace.steps
    };

    this.lastSummary = summary;

    try {
      console.groupCollapsed(`[cart-debug] ${summary.type} ${summary.totalMs}ms (${status})`);
      console.log('meta', summary.meta);
      console.table(summary.steps);
      if (Object.keys(meta).length) console.log('end', meta);
      console.groupEnd();
    } catch (_error) {}

    try {
      document.dispatchEvent(new CustomEvent('cart:debug-trace', {
        detail: summary
      }));
    } catch (_error) {}
  }
};

if (!window.__cartDebugFetchWrapped) {
  window.__cartDebugFetchWrapped = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = function cartDebugFetchWrapper(input, init) {
    const getUrl = () => {
      if (typeof input === 'string') return input;
      if (input && typeof input.url === 'string') return input.url;
      return '';
    };

    const url = getUrl();
    const isCartRequest = typeof url === 'string' && (
      url.indexOf('/cart') !== -1 ||
      url.indexOf('cart/change') !== -1 ||
      url.indexOf('cart/add') !== -1 ||
      url.indexOf('cart/update') !== -1
    );

    if (!isCartRequest || !window.CartActionDebug.enabled()) {
      return originalFetch(input, init);
    }

    const method = init && init.method
      ? init.method
      : (input && input.method ? input.method : 'GET');

    const trace = window.CartActionDebug.start('fetch-intercept', {
      url,
      method
    });

    window.CartActionDebug.step(trace, 'request-sent');
    try {
      console.trace('[cart-debug] fetch-initiator', method, url);
    } catch (_error) {}

    return originalFetch(input, init)
      .then((response) => {
        window.CartActionDebug.step(trace, 'response-received', {
          status: response.status,
          ok: response.ok
        });

        window.CartActionDebug.end(trace, response.ok ? 'ok' : 'http-error', {
          status: response.status
        });

        return response;
      })
      .catch((error) => {
        window.CartActionDebug.end(trace, 'error', {
          message: error && error.message ? error.message : 'fetch failed',
          url
        });
        throw error;
      });
  };
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    this.pendingCartTrace = null;
    this.pendingQuantityRequests = new Map();
    this.quantityRequestSeq = 0;
    this.lastQuantityIntent = null;

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  normalizeQuantityLine(line) {
    return String(line);
  }

  normalizeQuantityValue(quantity) {
    return String(quantity);
  }

  isCartActionDebugEnabled() {
    return !!(window.CartActionDebug && typeof window.CartActionDebug.enabled === 'function' && window.CartActionDebug.enabled());
  }

  logDuplicateSuppressed(label, meta) {
    if (!this.isCartActionDebugEnabled()) return;

    try {
      console.log(`[cart-debug] ${label}`, meta);
    } catch (_error) {}
  }

  shouldSuppressDuplicateIntent(line, quantity) {
    const now = Date.now();
    const intentLine = this.normalizeQuantityLine(line);
    const intentQuantity = this.normalizeQuantityValue(quantity);
    const suppressWindowMs = 150;

    if (
      this.lastQuantityIntent
      && this.lastQuantityIntent.line === intentLine
      && this.lastQuantityIntent.quantity === intentQuantity
      && (now - this.lastQuantityIntent.at) <= suppressWindowMs
    ) {
      this.logDuplicateSuppressed('duplicate-intent-suppressed', {
        line: intentLine,
        quantity: intentQuantity,
        source: 'onChange',
        elapsedMs: now - this.lastQuantityIntent.at
      });
      return true;
    }

    this.lastQuantityIntent = {
      line: intentLine,
      quantity: intentQuantity,
      at: now
    };

    return false;
  }

  registerPendingQuantityRequest(line, quantity) {
    const requestLine = this.normalizeQuantityLine(line);
    const requestQuantity = this.normalizeQuantityValue(quantity);
    const existing = this.pendingQuantityRequests.get(requestLine);

    if (existing && existing.quantity === requestQuantity) {
      this.logDuplicateSuppressed('duplicate-request-suppressed', {
        line: requestLine,
        quantity: requestQuantity,
        source: 'updateQuantity',
        elapsedMs: Date.now() - existing.startedAt
      });
      return null;
    }

    if (existing && existing.quantity !== requestQuantity && existing.abortController) {
      try { existing.abortController.abort(); } catch (_) {}
    }

    this.quantityRequestSeq += 1;
    const requestState = {
      quantity: requestQuantity,
      startedAt: Date.now(),
      requestId: this.quantityRequestSeq,
      abortController: typeof AbortController !== 'undefined' ? new AbortController() : null
    };

    this.pendingQuantityRequests.set(requestLine, requestState);
    return requestState;
  }

  clearPendingQuantityRequest(line, requestId) {
    const requestLine = this.normalizeQuantityLine(line);
    const existing = this.pendingQuantityRequests.get(requestLine);

    if (existing && existing.requestId === requestId) {
      this.pendingQuantityRequests.delete(requestLine);
    }
  }

  onChange(event) {
    const line = event.target.dataset.index;
    const quantity = event.target.value;

    if (this.shouldSuppressDuplicateIntent(line, quantity)) {
      return;
    }

    this.pendingCartTrace = window.CartActionDebug.start('quantity-change', {
      line,
      value: quantity,
      debounceMs: ON_CHANGE_DEBOUNCE_TIMER
    });
    window.CartActionDebug.step(this.pendingCartTrace, 'input-change');
    this.updateQuantity(line, quantity, document.activeElement.getAttribute('name'));
  }

  onCartUpdate() {
    const refreshId = this.cartRefreshId = (this.cartRefreshId || 0) + 1;
    const sections = this.getSectionsToRender();
    const trace = window.CartActionDebug.start('cart-refresh', {
      source: 'pub-sub'
    });
    window.CartActionDebug.step(trace, 'fetch-start');
    return fetch(`${routes.cart_url}?sections=${sections.map((section) => section.section).join(',')}`)
      .then((response) => {
        window.CartActionDebug.step(trace, 'fetch-response', {
          status: response.status
        });
        if (!response.ok) throw new Error('Cart refresh failed');
        return response.json();
      })
      .then((data) => {
        if (refreshId !== this.cartRefreshId || !this.isConnected) return;
        const contents = sections.map((section) => {
          const target = document.getElementById(section.id);
          return {
            target: target && (target.querySelector(section.selector) || target),
            html: this.getSectionInnerHTML(data[section.section], section.selector)
          };
        });
        contents.forEach(({ target, html }) => {
          if (target) target.innerHTML = html;
        });
        const empty = !this.querySelector('.cart-item');
        this.classList.toggle('is-empty', empty);
        const footer = document.getElementById('main-cart-footer');
        if (footer) footer.classList.toggle('is-empty', empty);
        window.CartActionDebug.step(trace, 'innerHTML-swap');
        window.CartActionDebug.end(trace);
      })
      .catch((error) => {
        window.CartActionDebug.end(trace, 'error', {
          message: error && error.message ? error.message : 'refresh failed'
        });
        console.error(error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents'
      }
    ];
  }

  getOfferQuantityChange(line, quantity) {
    if (!Number.isInteger(Number(quantity)) || Number(quantity) <= 0) return null;

    const data = this.querySelector('[data-offer-cart-lines]');
    if (!data) return null;
    const item = JSON.parse(data.textContent)[Number(line) - 1];
    if (!item) return null;
    const properties = Array.isArray(item.properties) ? Object.fromEntries(item.properties) : (item.properties || {});
    if (!Object.prototype.hasOwnProperty.call(properties, '_offer_tier_titles')
      && !Object.prototype.hasOwnProperty.call(properties, '_offer_tier_map')) return null;
    if (['_free_gift', '_offer_gift', '_subscription_gift', '_shipping_protection'].some((key) =>
      properties[key] === true || properties[key] === 'true')) return null;

    const tierValue = (table, reachedTier = false) => {
      const rows = String(table || '').split(';').filter((entry) => {
        const split = entry.indexOf(':');
        const threshold = Number(entry.slice(0, split));
        return split > 0 && threshold > 0
          && (reachedTier ? threshold <= Number(quantity) : threshold === Number(quantity));
      }).sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
      const row = rows[0];
      return row ? row.slice(row.indexOf(':') + 1) : '';
    };

    return {
      item,
      properties: {
        ...properties,
        _offer_boxes: String(quantity),
        _offer_tier: tierValue(properties._offer_tier_titles),
        _offer_gifts: tierValue(properties._offer_tier_map, true)
      }
    };
  }

  getQuantityResponseLine(state, line, offerChange) {
    if (!offerChange) return line;

    // Properties change the key and can move the line. Resolve it in the answer.
    return state.items.findIndex((item) => {
      if (item.variant_id !== offerChange.item.variant_id) return false;
      if ((item.selling_plan_allocation?.selling_plan?.id || null) !== offerChange.item.selling_plan_id) return false;
      const properties = item.properties || {};
      const keys = new Set([...Object.keys(properties), ...Object.keys(offerChange.properties)]);
      return [...keys].every((key) => (properties[key] ?? '') === (offerChange.properties[key] ?? ''));
    }) + 1;
  }

  updateQuantity(line, quantity, name) {
    const requestState = this.registerPendingQuantityRequest(line, quantity);
    if (!requestState) {
      this.pendingCartTrace = null;
      return;
    }
    this.cartRefreshId = (this.cartRefreshId || 0) + 1;

    const trace = this.pendingCartTrace || window.CartActionDebug.start('quantity-change', {
      line,
      quantity
    });
    let traceStatus = 'ok';
    let traceMeta = {};
    this.pendingCartTrace = null;
    window.CartActionDebug.step(trace, 'updateQuantity-enter', {
      line,
      quantity,
      trigger: name || 'unknown'
    });

    this.enableLoading(line);
    window.CartActionDebug.step(trace, 'loading-enabled');
    document.dispatchEvent(new CustomEvent('cart:drawer-loading-start', {
      detail: { minDuration: 0 }
    }));
    window.CartActionDebug.step(trace, 'drawer-loading-start-dispatched', {
      minDurationMs: 0
    });

    const offerChange = this.getOfferQuantityChange(line, quantity);
    const body = JSON.stringify({
      ...(offerChange ? { id: offerChange.item.key, properties: offerChange.properties } : { line }),
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });
    window.CartActionDebug.step(trace, 'request-body-ready', {
      bytes: body.length
    });

    const fetchSignal = requestState.abortController ? requestState.abortController.signal : undefined;
    window.CartActionDebug.step(trace, 'fetch-start');
    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body }, ...(fetchSignal ? { signal: fetchSignal } : {}) })
      .then((response) => {
        window.CartActionDebug.step(trace, 'fetch-response', {
          status: response.status
        });
        return response.text();
      })
      .then((state) => {
        window.CartActionDebug.step(trace, 'response-text', {
          bytes: state.length
        });
        const parsedState = JSON.parse(state);
        window.CartActionDebug.step(trace, 'json-parse', {
          itemCount: parsedState.item_count
        });
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          window.CartActionDebug.step(trace, 'parsed-errors');
          traceStatus = 'error';
          traceMeta = {
            message: parsedState.errors
          };
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const activeDrawer = cartDrawerWrapper?.classList.contains('active') ? cartDrawerWrapper : null;
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper && !activeDrawer) cartDrawerWrapper.firstOpenRefreshDone = false;

        window.CartActionDebug.step(trace, 'section-render-start');
        this.getSectionsToRender().forEach((section => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          window.CartActionDebug.step(trace, `section:${section.section || section.id}`);
        }));
        window.CartActionDebug.step(trace, 'section-render-end');
        const responseLine = this.getQuantityResponseLine(parsedState, line, offerChange);
        const updatedValue = parsedState.items[responseLine - 1]?.quantity;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(responseLine, message);

        const lineItem = document.getElementById(`CartItem-${responseLine}`) || document.getElementById(`CartDrawer-Item-${responseLine}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          activeDrawer ? trapFocus(activeDrawer, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && activeDrawer) {
          trapFocus(activeDrawer.querySelector('.drawer__inner-empty'), activeDrawer.querySelector('a'))
        } else if (document.querySelector('.cart-item') && activeDrawer) {
          trapFocus(activeDrawer, document.querySelector('.cart-item__name'))
        }
        window.CartActionDebug.step(trace, 'focus-restored');
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-items'});
        window.CartActionDebug.step(trace, 'pubsub-published');
      }).catch((err) => {
        if (err && err.name === 'AbortError') return;
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
        traceStatus = 'error';
        traceMeta = {
          message: window.cartStrings.error
        };
      })
      .finally(() => {
        const wasAborted = fetchSignal && fetchSignal.aborted;
        document.dispatchEvent(new CustomEvent('cart:drawer-loading-stop'));
        if (!wasAborted) {
          this.disableLoading(line);
          this.clearPendingQuantityRequest(line, requestState.requestId);
          window.CartActionDebug.step(trace, 'loading-disabled');
          window.CartActionDebug.end(trace, traceStatus, traceMeta);
        }
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
      constructor() {
        super();

      this.addEventListener('change', debounce((event) => {
            const trace = window.CartActionDebug.start('cart-note-change');
            window.CartActionDebug.step(trace, 'note-change');
            const body = JSON.stringify({ note: event.target.value });
            window.CartActionDebug.step(trace, 'request-body-ready', {
              bytes: body.length
            });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then((response) => {
                window.CartActionDebug.step(trace, 'fetch-response', {
                  status: response.status
                });
                window.CartActionDebug.end(trace);
                return response;
              })
              .catch((error) => {
                window.CartActionDebug.end(trace, 'error', {
                  message: error && error.message ? error.message : 'note update failed'
                });
              });
      }, ON_CHANGE_DEBOUNCE_TIMER))
      }
  });
};

function handleDiscountForm(e) {
  e.preventDefault();

  const discountInput = e.target.querySelector('[name=cart-discount-field]');
  const discountError = e.target.querySelector('.cart-discount-form__error');
  const discountValue = discountInput.value;
  if (discountValue === undefined || discountValue.length === 0) {
    discountError.style.display = 'block';
    return;
  }
  discountError.style.display = 'none';
  const checkoutBaseUrl = '/checkout?discount=';
  const newCheckoutUrl = checkoutBaseUrl + discountValue;
  window.location.href = newCheckoutUrl;
}

function handleDiscountFormChange(e) {
  const discountErros = document.querySelectorAll('.cart-discount-form__error');
  discountErros.forEach(error => {
    error.style.display = 'none'
  })
}
